/**
 * LayerPicker — Design B (Bottom Sheet Grid) implementation of the
 * basemap and overlay switcher.
 *
 * Replaces the ol-ext LayerSwitcher with a touch-friendly grid of large
 * preview cards rendered into the existing draggable #bottom-sheet
 * widget. Triggered by the Layers dock button.
 *
 * Layer ordering follows MapView.createBaseLayers() — the OpenLayers
 * map is the single source of truth; this component walks the map's
 * layer tree on every open() to stay in sync with any external changes.
 */

import { t } from '../i18n.js';

// localStorage key used to persist the user's most recent basemap choice.
// The value is the layer's `title` string ('Satellite', 'Carto Dark', ...),
// which is the same identifier the picker uses internally, so no
// title-to-key translation is needed.
const STORAGE_KEY = 'bw.layerpicker.baseTitle';

// Visual previews for each known basemap. Keys match the `title` property
// set on each TileLayer in MapView.createBaseLayers(). Values are CSS
// background-image expressions; for known basemaps these point at PNG
// screenshots in /public/icons. Unknown layer titles fall back to a
// generic gradient so a layer added later still renders.
// Vite injects BASE_URL at build time: '/' for normal builds, '/src/' for
// the production sub-path build (`npm run build:subpath`). Always ends in
// a slash, so we concatenate without a leading '/' on the right side.
const B = import.meta.env.BASE_URL;
const PREVIEWS = {
  'OpenStreetMap': `url('${B}img/OSM.webp')`,
  'Carto Light':   `url('${B}img/Light.webp')`,
  'Carto Dark':    `url('${B}img/Dark.webp')`,
  'Satellite':     `url('${B}img/Satellite.webp')`,
  'Topographic':   `url('${B}img/Topography.webp')`,
  'OSM Cycle map': `url('${B}img/OSMCycle.webp')`,
  'Google Sat':    `url('${B}img/GoogleSat.webp')`,
};
const FALLBACK_PREVIEW = 'linear-gradient(135deg, #e0e0e0, #b0b0b0)';

// Friendly display names. Falls back to layer.title if not listed.
const DISPLAY_NAMES = {
  'OpenStreetMap': 'OSM',
  'OSM Cycle map': 'OSM Cycle',
};

// Overlay friendly names + icons (Bootstrap Icons class names).
const OVERLAY_META = {
  'Overlays / POI':    { label: 'POI',       icon: 'bi-shop' },
  'Overlays / Markers':{ label: 'My pins',   icon: 'bi-pin-map' },
};

export class LayerPicker {
  /**
   * @param {object} mapView   the MapView instance (must expose getMap())
   * @param {object} bottomSheet  the bottomSheet controller from main.js
   *                              (must expose open(state), body, and a header H5)
   */
  constructor(mapView, bottomSheet) {
    this.mapView = mapView;
    this.bottomSheet = bottomSheet;
  }

  /**
   * Inject the picker UI into the bottom sheet and open it to half-screen.
   * Called from the Layers dock button click handler.
   */
  open() {
    const map = this.mapView?.getMap?.();
    const body = this.bottomSheet?.body;
    if (!map || !body) {
      console.warn('[LayerPicker] map or bottom sheet not ready');
      return;
    }

    this._renderHeader();
    this._renderBody(body);
    this._wireEvents(body);

    this.bottomSheet.open('half');
  }

  // ---------- render ----------

  _renderHeader() {
    const label = document.getElementById('bottomSheetLabel');
    if (!label) return;
    const title = (typeof t === 'function' && t('layers.title')) || 'Map layers';
    label.innerHTML = `<i class="bi bi-stack me-2"></i><span>${_esc(title)}</span>`;
  }

  _renderBody(body) {
    const { bases, overlays } = this._collectLayers();

    const cards = bases.map((b) => this._cardHtml(b)).join('');
    const chips = overlays.map((o) => this._chipHtml(o)).join('');

    body.innerHTML = `
      <div class="layerpicker">
        <div class="layerpicker-grid">${cards}</div>
        ${overlays.length ? `
          <div class="layerpicker-overlay-label">Overlays</div>
          <div class="layerpicker-chips">${chips}</div>
        ` : ''}
      </div>
    `;
    _ensureStyles();
  }

  // ---------- event wiring ----------

  _wireEvents(body) {
    body.querySelectorAll('.layerpicker-card').forEach((el) => {
      el.addEventListener('click', () => {
        const title = el.dataset.layer;
        this._selectBase(title);
      });
    });
    body.querySelectorAll('.layerpicker-chip').forEach((el) => {
      el.addEventListener('click', () => {
        const path = el.dataset.layer;
        this._toggleOverlay(path);
      });
    });
  }

  // ---------- layer manipulation ----------

  _selectBase(title) {
    const { bases } = this._collectLayers();
    for (const b of bases) {
      b.layer.setVisible(b.title === title);
    }
    // Persist the choice so the same basemap is restored on next launch.
    try { localStorage.setItem(STORAGE_KEY, title); } catch (_) {}
    // Re-render so the active highlight updates without reopening
    this._renderBody(this.bottomSheet.body);
    this._wireEvents(this.bottomSheet.body);
  }

  /**
   * Apply the persisted basemap selection to a freshly-constructed MapView.
   * Call this from main.js immediately after `new MapView(...)` so the saved
   * choice is applied before the first paint. Safe no-op if no preference
   * has been stored yet or if the stored title no longer matches any layer.
   *
   * Static so callers don't need to instantiate the full picker just to
   * restore — the picker itself is wired later, once the dock button exists.
   *
   * @param {object} mapView  the MapView instance (must expose getMap())
   */
  static restorePersisted(mapView) {
    let saved;
    try { saved = localStorage.getItem(STORAGE_KEY); } catch (_) {}
    if (!saved) return;

    const map = mapView?.getMap?.();
    if (!map) return;

    let matched = false;
    map.getLayers().forEach((layer) => {
      if (typeof layer.getLayers !== 'function') return;
      const groupTitle = (layer.get('title') || '').toLowerCase();
      if (!groupTitle.includes('base')) return;
      layer.getLayers().forEach((child) => {
        const isMatch = child.get('title') === saved;
        if (isMatch) matched = true;
        child.setVisible(isMatch);
      });
    });

    if (!matched) {
      console.warn(`[LayerPicker] Persisted basemap "${saved}" not found; keeping default.`);
    }
  }

  _toggleOverlay(path) {
    const { overlays } = this._collectLayers();
    const o = overlays.find((x) => x.path === path);
    if (!o) return;
    o.layer.setVisible(!o.layer.getVisible());
    this._renderBody(this.bottomSheet.body);
    this._wireEvents(this.bottomSheet.body);
  }

  // ---------- map introspection ----------

  /**
   * Walk the map's layer tree. We expect the structure:
   *   map
   *   ├── LayerGroup "Base Maps"  (one child visible at a time)
   *   └── LayerGroup "Overlays"   (any number visible)
   *
   * Anything else is silently ignored.
   */
  _collectLayers() {
    const map = this.mapView.getMap();
    const bases = [];
    const overlays = [];

    map.getLayers().forEach((layer) => {
      // ol/layer/Group exposes its children via getLayers().
      const isGroup = typeof layer.getLayers === 'function';
      if (!isGroup) return;
      const groupTitle = layer.get('title') || '';
      layer.getLayers().forEach((child) => {
        const title = child.get('title') || 'Untitled';
        const entry = {
          title,
          displayTitle: DISPLAY_NAMES[title] || title,
          isActive: child.getVisible(),
          layer: child,
        };
        if (groupTitle.toLowerCase().includes('base')) {
          bases.push(entry);
        } else {
          entry.path = `${groupTitle} / ${title}`;
          overlays.push(entry);
        }
      });
    });

    return { bases, overlays };
  }

  // ---------- HTML builders ----------

  _cardHtml(b) {
    const preview = PREVIEWS[b.title] || FALLBACK_PREVIEW;
    const activeCls = b.isActive ? 'is-active' : '';
    const check = b.isActive ? '<span class="layerpicker-check"><i class="bi bi-check-lg"></i></span>' : '';
    return `
      <button type="button" class="layerpicker-card ${activeCls}" data-layer="${_esc(b.title)}">
        <span class="layerpicker-preview" style="background-image:${preview};"></span>
        <span class="layerpicker-name">${_esc(b.displayTitle)}</span>
        ${check}
      </button>
    `;
  }

  _chipHtml(o) {
    const meta = OVERLAY_META[o.path] || { label: o.title, icon: 'bi-layers' };
    const activeCls = o.isActive ? 'is-active' : '';
    return `
      <button type="button" class="layerpicker-chip ${activeCls}" data-layer="${_esc(o.path)}">
        <i class="bi ${_esc(meta.icon)}"></i>
        <span>${_esc(meta.label)}</span>
      </button>
    `;
  }
}

// ---------- utilities ----------

function _esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

/**
 * Inject the component's CSS once (idempotent). Keeps the styles
 * co-located with the component so adopters don't have to touch the
 * global stylesheet.
 */
let _stylesInjected = false;
function _ensureStyles() {
  if (_stylesInjected) return;
  _stylesInjected = true;
  const css = `
    .layerpicker { padding: 4px 4px 16px; }
    .layerpicker-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
      gap: 10px;
    }
    .layerpicker-card {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      padding: 0;
      background: #fff;
      border: 2px solid #e0e0e0;
      border-radius: 10px;
      overflow: hidden;
      cursor: pointer;
      transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    }
    .layerpicker-card:active { transform: scale(0.97); }
    .layerpicker-card:hover  { border-color: #B040B0; }
    .layerpicker-card.is-active {
      border-color: #8B008B;
      box-shadow: 0 3px 10px rgba(139, 0, 139, 0.18);
    }
    .layerpicker-preview {
      display: block;
      width: 100%;
      aspect-ratio: 4 / 3;
      background-size: cover;
      background-position: center;
    }
    .layerpicker-name {
      display: block;
      padding: 8px 10px;
      font-size: 13px;
      font-weight: 600;
      color: #1A1A1A;
      text-align: left;
    }
    .layerpicker-card.is-active .layerpicker-name {
      color: #8B008B;
    }
    .layerpicker-check {
      position: absolute;
      top: 6px;
      right: 6px;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #8B008B;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }
    .layerpicker-overlay-label {
      margin: 16px 4px 6px;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #666;
    }
    .layerpicker-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    .layerpicker-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      background: #f0f0f0;
      color: #666;
      border: none;
      border-radius: 999px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.12s ease, color 0.12s ease;
      -webkit-tap-highlight-color: transparent;
    }
    .layerpicker-chip i { font-size: 14px; }
    .layerpicker-chip:hover { background: #e2e2e2; }
    .layerpicker-chip.is-active {
      background: #FFE96A;
      color: #1A1A1A;
    }
  `;
  const styleEl = document.createElement('style');
  styleEl.id = 'layerpicker-styles';
  styleEl.textContent = css;
  document.head.appendChild(styleEl);
}

export default { LayerPicker };
